import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, PawPrint, Sparkles } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";
import { ColorCountBadge } from "@/lib/shop";
import { productName, productImg, AvailabilityBadge, PriceTag } from "@/pages/ShopPage";

// Homepage boutique showcase — spotlight carousel: one product takes the stage,
// autoplay with pause-on-hover, arrows + dots, animated transitions
export const BoutiqueShowcase = () => {
  const { t, lang } = useLang();
  const s = t.shop;
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [badgeOn, setBadgeOn] = useState(false);
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const hoverRef = useRef(false);

  useEffect(() => {
    api.get("/shop/featured").then((r) => setData(r.data)).catch(() => setData({ show: false, products: [] }));
    api.get("/shop/status").then((r) => setBadgeOn(!!r.data?.colors_badge)).catch(() => {});
  }, []);

  const n = data?.products?.length || 0;
  const go = useCallback(
    (next, d) => {
      if (!n) return;
      setDir(d ?? (next > idx ? 1 : -1));
      setIdx(((next % n) + n) % n);
    },
    [n, idx]
  );

  // Autoplay — pauses while the visitor hovers the stage
  useEffect(() => {
    if (!n) return;
    const tmr = setInterval(() => {
      if (!hoverRef.current) go(idx + 1, 1);
    }, 6000);
    return () => clearInterval(tmr);
  }, [n, idx, go]);

  if (!data || !data.show || n === 0) return null;
  const p = data.products[idx];

  return (
    <section id="boutique" className="relative py-24 sm:py-32 overflow-hidden bg-gradient-to-b from-brand-soft/40 via-brand-cream to-background" data-testid="home-boutique">
      <PawPrint className="absolute top-16 -left-10 h-48 w-48 text-brand/[0.04] -rotate-12 pointer-events-none" />
      <PawPrint className="absolute bottom-10 -right-8 h-40 w-40 text-bamboo/[0.06] rotate-12 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-brand-soft text-brand px-4 py-1.5 text-xs font-bold uppercase tracking-wide">
              <Sparkles className="h-3.5 w-3.5" /> {s.home_eyebrow}
            </span>
            <h2 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-ink leading-[1.05]">{s.home_title}</h2>
            <p className="mt-3 max-w-lg text-brand-muted">{s.home_sub}</p>
          </motion.div>
          <button
            onClick={() => navigate("/shop")}
            className="inline-flex items-center gap-2 rounded-full bg-brand text-brand-cream px-6 h-12 font-bold text-sm hover:bg-brand-hover transition-colors no-a11y-underline"
            data-testid="boutique-view-all"
          >
            {s.home_view_all} <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        {/* The stage */}
        <div
          className="mt-12 relative rounded-[2rem] bg-white border border-brand/10 shadow-[0_18px_60px_rgba(134,77,99,0.14)] overflow-hidden"
          onMouseEnter={() => (hoverRef.current = true)}
          onMouseLeave={() => (hoverRef.current = false)}
          data-testid="boutique-stage"
        >
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={p.id}
              custom={dir}
              initial={{ opacity: 0, x: dir * 60 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: dir * -60 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
              className="grid md:grid-cols-2"
            >
              {/* Image side */}
              <button
                onClick={() => navigate(`/shop/product/${p.id}`)}
                className="group relative aspect-[4/3] md:aspect-auto md:min-h-[420px] bg-stone-alt/50 overflow-hidden no-a11y-underline"
                data-testid={`featured-${p.id}`}
              >
                {productImg(p) ? (
                  <img src={productImg(p)} alt={productName(p, lang)} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" draggable={false} />
                ) : (
                  <div className="absolute inset-0 grid place-items-center text-brand/20">
                    <PawPrint className="h-24 w-24" />
                  </div>
                )}
                {badgeOn && <ColorCountBadge count={p.color_count} lang={lang} />}
                {p.on_sale && (
                  <span className="absolute top-4 left-4 rounded-full bg-rose-500 text-white text-xs font-bold px-3 py-1.5 shadow-md" data-testid="featured-sale-badge">
                    {lang === "el" ? "ΠΡΟΣΦΟΡΑ" : "SALE"}
                  </span>
                )}
              </button>

              {/* Info side */}
              <div className="relative p-8 sm:p-12 flex flex-col justify-center">
                {p.brand && <p className="text-xs font-bold uppercase tracking-widest text-brand-ink/40">{p.brand}</p>}
                <h3 className="mt-2 font-heading text-3xl sm:text-4xl font-bold text-brand-ink leading-tight">{productName(p, lang)}</h3>
                <div className="mt-4 text-xl"><PriceTag p={p} /></div>
                <div className="mt-3"><AvailabilityBadge availability={p.availability} lead={p.lead_days} /></div>
                <div className="mt-8 flex items-center gap-3">
                  <button
                    onClick={() => navigate(`/shop/product/${p.id}`)}
                    className="rounded-full bg-brand text-brand-cream px-7 h-12 font-bold text-sm hover:bg-brand-hover transition-colors no-a11y-underline"
                    data-testid="featured-cta"
                  >
                    {s.details}
                  </button>
                </div>
                {/* Counter */}
                <p className="absolute top-6 right-8 text-sm font-bold text-brand-ink/30" data-testid="featured-counter">
                  {idx + 1} / {n}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Arrows */}
          <button
            onClick={() => go(idx - 1, -1)}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 grid place-items-center h-11 w-11 rounded-full bg-white/90 backdrop-blur-sm border border-brand/15 text-brand shadow-md hover:bg-brand hover:text-brand-cream transition-colors no-a11y-underline"
            data-testid="boutique-prev"
            aria-label="Previous"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <button
            onClick={() => go(idx + 1, 1)}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 grid place-items-center h-11 w-11 rounded-full bg-white/90 backdrop-blur-sm border border-brand/15 text-brand shadow-md hover:bg-brand hover:text-brand-cream transition-colors no-a11y-underline"
            data-testid="boutique-next"
            aria-label="Next"
          >
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>

        {/* Dots + thumbnails */}
        <div className="mt-6 flex items-center justify-center gap-2" data-testid="boutique-dots">
          {data.products.map((pp, i) => (
            <button
              key={pp.id}
              onClick={() => go(i)}
              aria-label={productName(pp, lang)}
              className={`relative rounded-full transition-all duration-300 no-a11y-underline ${i === idx ? "w-10 h-3 bg-brand" : "w-3 h-3 bg-brand/25 hover:bg-brand/50"}`}
              data-testid={`boutique-dot-${i}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default BoutiqueShowcase;
